import { ANSWER_MARKDOWN_MAX, ANSWER_PROMPT_MAX } from "@kourai/api/home-answer";
import { readVisual } from "@/lib/visuals";

/** The parts of a chat message this reads (assistant-ui's message parts). */
type Part = { type: string; text?: string; toolName?: string; args?: unknown };

export type PinnedAnswer = {
  title: string;
  prompt: string;
  markdown: string;
  visual?: unknown;
};

/** At most this long, cut on a word. */
const ANSWER_TITLE_MAX = 60;

function shorten(s: string, max: number) {
  const clean = s.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), max / 2))}…`;
}

/**
 * An answer, ready to pin to the board (ADMIN-266): its text, its first visual, and the question
 * that asked it (so it can be asked again on a schedule). Null when there's nothing to keep.
 */
export function answerFromMessage(parts: readonly Part[], question: string): PinnedAnswer | null {
  const markdown = parts
    .filter((p) => p.type === "text" && typeof p.text === "string")
    .map((p) => p.text)
    .join("\n\n")
    .trim()
    .slice(0, ANSWER_MARKDOWN_MAX);
  const call = parts.find((p) => p.type === "tool-call" && p.toolName === "show_visual");
  const visual = call ? readVisual(call.args) : null;
  if (!markdown && !visual) return null;
  const prompt = question.trim().slice(0, ANSWER_PROMPT_MAX);
  const heading = markdown.match(/^#{1,3}\s+(.+)$/m)?.[1];
  const title = shorten(visual?.title ?? heading ?? (prompt || "Pinned answer"), ANSWER_TITLE_MAX);
  return { title, prompt, markdown, ...(visual ? { visual: call?.args } : {}) };
}

/** Whether a pinned answer is due to be asked again. */
export function isStale(answeredAt: string | undefined, refreshHours: number, now = Date.now()) {
  if (!refreshHours || !answeredAt) return false;
  const at = Date.parse(answeredAt);
  return Number.isFinite(at) && now - at >= refreshHours * 60 * 60 * 1000;
}

/** "just now", "12 min ago", "3 h ago", "2 days ago". */
export function answeredAgo(answeredAt: string | undefined, now = Date.now()) {
  const at = answeredAt ? Date.parse(answeredAt) : Number.NaN;
  if (!Number.isFinite(at)) return "";
  const min = Math.max(0, Math.round((now - at) / 60_000));
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}
